import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import CourseEditor from '../../components/features/textEditor/textEditor';
import { useColorContext } from '../../context/colorContext';
import { useLanguageContext } from '../../context/languageContext';
import { useNoteDispatch, useNoteState } from '../../context/noteContexts/noteContext';
import { useUserLimitDispatch, useUserLimitState } from '../../context/user_limit_context';
import { useNavigateContext } from '../../context/NavigateContext';
import { FiChevronRight } from 'react-icons/fi';
import AIChatFloatingButton from '../../components/features/AIchat/AIchatElements';
import AIChat from '../../components/features/AIchat/AIchat';
import {
	appendUserMessageToConversationJson,
	sendChatConversationToBackend,
} from '../../utils/chatConversation';
import { extractPlainTextFromTipTap } from '../../utils/tiptapPlainText';

const EMPTY_DOC = {
	type: 'doc',
	content: [{ type: 'paragraph' }],
};

const cloneTipTapJson = (value) => {
	if (!value || typeof value !== 'object') return value;
	try {
		return JSON.parse(JSON.stringify(value));
	} catch {
		return value;
	}
};

const toSafeHex = (hex, fallback) => {
	const value = String(hex || '').trim();
	return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
	const safeHex = toSafeHex(hex, fallback);
	const r = parseInt(safeHex.slice(1, 3), 16);
	const g = parseInt(safeHex.slice(3, 5), 16);
	const b = parseInt(safeHex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function NoteEditorView() {
	const {
		EditorContent_json_tiptap,
		note_id,
		if_expect_source_content_parsing,
		if_source_content_parsing_finished,
		if_loading_note_content,
		chat_conversation_json,
	} = useNoteState();
	const {
		setEditorContent_json_tiptap,
		setChat_conversation_json,
		registerEditorContentGetter,
	} = useNoteDispatch();
	const { incrementChatConversation, openUpgradePopup } = useUserLimitDispatch();
	const { isChatLimitReached, isAdaptionLimitReached, adaptionLimit } = useUserLimitState();
	const { setValuesAsAdaptedView } = useNavigateContext();
	const { t } = useLanguageContext();
	const {
		bgPrimary,
		bgSecondary,
		bgTertiary,
		textPrimary,
		textSecondary,
		textTertiary,
		activeAccent,
	} = useColorContext();

	// State declarations must come first before any computed values that depend on them
	const [isBottomBarCollapsed, setIsBottomBarCollapsed] = useState(false);
	const [isChatOpen, setIsChatOpen] = useState(false);
	const [isChatFullscreen, setIsChatFullscreen] = useState(false);
	const [isChatVisible, setIsChatVisible] = useState(false);
	const [isChatAnimatedIn, setIsChatAnimatedIn] = useState(false);
	const [isChatSubmitting, setIsChatSubmitting] = useState(false);
	const [chatPanelWidth, setChatPanelWidth] = useState(430);
	const [chatViewportWidth, setChatViewportWidth] = useState(() => (typeof window === 'undefined' ? 1280 : window.innerWidth));
	const isChatResizingRef = useRef(false);
	const chatAnimationFrameRef = useRef(null);
	const editorRef = useRef(null);
	const [isEditorMountReady, setIsEditorMountReady] = useState(false);

	useEffect(() => {
		registerEditorContentGetter(() => editorRef.current?.getCurrentContent?.() ?? null);
		return () => registerEditorContentGetter(null);
	}, [registerEditorContentGetter]);

	const CHAT_PANEL_MIN_WIDTH = 320;
	const CHAT_PANEL_MAX_WIDTH = 760;
	const CHAT_PANEL_RIGHT_OFFSET = 14;
	const CHAT_PANEL_SCREEN_MARGIN = 28;

	const resolvedEditorContent = EditorContent_json_tiptap || EMPTY_DOC;
	const editorInstanceKey = useMemo(() => {
		// Remount on note change to guarantee content reinjection after URL-based hydration.
		const noteKey = String(note_id || 'no-note');
		const phaseKey = if_source_content_parsing_finished ? 'ready' : 'pending';
		return `editor-${noteKey}-${phaseKey}`;
	}, [note_id, if_source_content_parsing_finished]);

	const safeBgPrimary = toSafeHex(bgPrimary, '#111111');
	const safeBgSecondary = toSafeHex(bgSecondary, '#191919');
	const safeBgTertiary = toSafeHex(bgTertiary, '#1E1E1E');
	const safeTextPrimary = toSafeHex(textPrimary, '#F0F0F0');
	const safeTextSecondary = toSafeHex(textSecondary, '#BDBDBD');
	const safeTextTertiary = safeTextPrimary;
	const safeAccent = toSafeHex(activeAccent, '#3B82F6');
	const actionButtonTextColor = '#F8FAFC';
	const toolbarHeightPx = 62;
	const bottomBarHeightPx = toolbarHeightPx;
	const viewportHeight = '100%';

	const pageBackground = useMemo(() => {
		return [
			`radial-gradient(90% 55% at 85% 5%, ${hexToRgba(safeAccent, 0.16, safeAccent)} 0%, transparent 72%)`,
			`linear-gradient(180deg, ${safeBgPrimary} 0%, ${hexToRgba(safeBgPrimary, 0.98, safeBgPrimary)} 100%)`,
		].join(', ');
	}, [safeAccent, safeBgPrimary]);

	const shouldShowParsingOverlay = if_expect_source_content_parsing && !if_source_content_parsing_finished;
	const shouldShowDatabaseLoadingOverlay = Boolean(if_loading_note_content);
	const loadingTitle = t('notes.loadingTitle');
	const loadingSubtitle = t('notes.loadingSubtitle');

	const handleAdaptClick = useCallback(() => {
		if (isAdaptionLimitReached) {
			openUpgradePopup(`You have reached the limit of ${adaptionLimit} adaptations on your current plan. Upgrade to continue.`);
			return;
		}
		editorRef.current?.manualSync?.();
		editorRef.current?.flushChanges?.();
		const currentEditorContent = editorRef.current?.getCurrentContent?.();

		if (currentEditorContent) {
			setEditorContent_json_tiptap(cloneTipTapJson(currentEditorContent));
		}
		Promise.resolve().then(() => {
			setValuesAsAdaptedView();
		});
	}, [isAdaptionLimitReached, openUpgradePopup, setEditorContent_json_tiptap, setValuesAsAdaptedView]);

	const handleChatSubmit = useCallback(async (messageText) => {
		if (isChatLimitReached) {
			openUpgradePopup("You have reached the free plan limit of 10 conversations. Upgrade to continue.");
			return;
		}
		const nextConversation = appendUserMessageToConversationJson(chat_conversation_json, messageText);
		setChat_conversation_json(nextConversation);
		const sourceText = extractPlainTextFromTipTap(EditorContent_json_tiptap);

		try {
			setIsChatSubmitting(true);
			const updatedConversation = await sendChatConversationToBackend(nextConversation, {
				noteId: note_id,
				sourceText,
				sourceType: 'editor-content',
				sourceFormat: 'tiptap-plain-text',
			});
			if (updatedConversation) {
				setChat_conversation_json(updatedConversation);
				incrementChatConversation();
			}
		} catch (error) {
			console.error('Chat request failed in NoteEditorView:', error);
		} finally {
			setIsChatSubmitting(false);
		}
	}, [isChatLimitReached, openUpgradePopup, chat_conversation_json, setChat_conversation_json, EditorContent_json_tiptap, note_id, incrementChatConversation]);

	useEffect(() => {
		let timeoutId = null;
		let animationFrameId = null;
		if (isChatOpen) {
			setIsChatVisible(true);
			setIsChatAnimatedIn(false);
			if (chatAnimationFrameRef.current) {
				window.cancelAnimationFrame(chatAnimationFrameRef.current);
			}
			animationFrameId = window.requestAnimationFrame(() => {
				chatAnimationFrameRef.current = window.requestAnimationFrame(() => {
					setIsChatAnimatedIn(true);
				});
			});
			return undefined;
		}

		setIsChatAnimatedIn(false);
		if (chatAnimationFrameRef.current) {
			window.cancelAnimationFrame(chatAnimationFrameRef.current);
			chatAnimationFrameRef.current = null;
		}
		timeoutId = window.setTimeout(() => {
			setIsChatVisible(false);
		}, 280);

		return () => {
			if (animationFrameId) {
				window.cancelAnimationFrame(animationFrameId);
			}
			if (chatAnimationFrameRef.current) {
				window.cancelAnimationFrame(chatAnimationFrameRef.current);
				chatAnimationFrameRef.current = null;
			}
			if (timeoutId) {
				window.clearTimeout(timeoutId);
			}
		};
	}, [isChatOpen]);

	useEffect(() => {
		const handleResize = () => {
			setChatViewportWidth(window.innerWidth);
		};

		handleResize();
		window.addEventListener('resize', handleResize);

		return () => {
			window.removeEventListener('resize', handleResize);
		};
	}, []);

	const chatFullscreenWidth = Math.max(
		CHAT_PANEL_MIN_WIDTH,
		chatViewportWidth - CHAT_PANEL_SCREEN_MARGIN,
	);
	const chatPanelRenderedWidth = isChatFullscreen ? chatFullscreenWidth : chatPanelWidth;
	const chatPanelLeft = isChatFullscreen
		? CHAT_PANEL_SCREEN_MARGIN / 2
		: Math.max(CHAT_PANEL_SCREEN_MARGIN / 2, chatViewportWidth - chatPanelRenderedWidth - CHAT_PANEL_RIGHT_OFFSET);

	const clampChatPanelWidth = useCallback((rawWidth) => {
		const fallbackMax = CHAT_PANEL_MAX_WIDTH;
		const viewportMax = typeof window === 'undefined'
			? fallbackMax
			: Math.max(CHAT_PANEL_MIN_WIDTH, window.innerWidth - CHAT_PANEL_SCREEN_MARGIN);
		const maxAllowed = Math.min(CHAT_PANEL_MAX_WIDTH, viewportMax);
		return Math.max(CHAT_PANEL_MIN_WIDTH, Math.min(maxAllowed, rawWidth));
	}, [CHAT_PANEL_MAX_WIDTH, CHAT_PANEL_MIN_WIDTH, CHAT_PANEL_SCREEN_MARGIN]);

	const handleChatResizeStart = useCallback((event) => {
		event.preventDefault();
		isChatResizingRef.current = true;
	}, []);

	useEffect(() => {
		let firstFrameId = 0;
		let secondFrameId = 0;

		setIsEditorMountReady(false);
		firstFrameId = window.requestAnimationFrame(() => {
			secondFrameId = window.requestAnimationFrame(() => {
				setIsEditorMountReady(true);
			});
		});

		return () => {
			if (firstFrameId) {
				window.cancelAnimationFrame(firstFrameId);
			}
			if (secondFrameId) {
				window.cancelAnimationFrame(secondFrameId);
			}
		};
	}, [editorInstanceKey]);

	useEffect(() => {
		const handleMouseMove = (event) => {
			if (!isChatResizingRef.current) return;
			const nextWidth = window.innerWidth - event.clientX - CHAT_PANEL_RIGHT_OFFSET;
			setChatPanelWidth(clampChatPanelWidth(nextWidth));
		};

		const handleMouseUp = () => {
			if (!isChatResizingRef.current) return;
			isChatResizingRef.current = false;
		};

		window.addEventListener('mousemove', handleMouseMove);
		window.addEventListener('mouseup', handleMouseUp);

		return () => {
			window.removeEventListener('mousemove', handleMouseMove);
			window.removeEventListener('mouseup', handleMouseUp);
		};
	}, [CHAT_PANEL_RIGHT_OFFSET, clampChatPanelWidth]);

	useEffect(() => {
		const handleWindowResize = () => {
			setChatPanelWidth((previousWidth) => clampChatPanelWidth(previousWidth));
		};

		window.addEventListener('resize', handleWindowResize);
		return () => window.removeEventListener('resize', handleWindowResize);
	}, [clampChatPanelWidth]);

	return (
		<div
			style={{
				height: viewportHeight,
				width: '100%',
				display: 'flex',
				overflow: 'hidden',
			}}
		>
			<style>
				{`
					@keyframes plynotePremiumShimmer {
						0% { transform: translateX(-130%); }
						100% { transform: translateX(130%); }
					}
				`}
			</style>

			<main
				style={{
					flex: 1,
					position: 'relative',
					display: 'flex',
					flexDirection: 'column',
					justifyContent: 'space-between',
					alignItems: 'stretch',
					height: viewportHeight,
					minHeight: viewportHeight,
					maxHeight: viewportHeight,
					background: pageBackground,
					overflow: 'hidden',
				}}
			>
					{shouldShowDatabaseLoadingOverlay && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								zIndex: 80,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 'clamp(20px, 5vw, 64px) clamp(16px, 4vw, 48px)',
								background: hexToRgba(safeBgPrimary, 0.88, safeBgPrimary),
								backdropFilter: 'blur(10px) saturate(110%)',
								WebkitBackdropFilter: 'blur(10px) saturate(110%)',
							}}
						>
							<div
								style={{
									width: 'min(90vw, 640px)',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: 'clamp(8px, 1.4vh, 12px)',
								}}
							>
								<div style={{ fontSize: 'clamp(1.04rem, 1.75vw, 1.42rem)', fontWeight: 700, letterSpacing: '0.008em', color: safeTextPrimary, textAlign: 'center', lineHeight: 1.2 }}>
									{loadingTitle}
								</div>
								<div style={{ fontSize: 'clamp(0.8rem, 1.05vw, 0.92rem)', lineHeight: 1.5, color: safeTextSecondary, maxWidth: 560, textAlign: 'center' }}>
									{loadingSubtitle}
								</div>
								<div
									style={{
										position: 'relative',
										width: 'min(74vw, 460px)',
										height: 'clamp(4px, 0.55vw, 6px)',
										borderRadius: 999,
										overflow: 'hidden',
										background: hexToRgba(safeTextSecondary, 0.18, safeTextSecondary),
									}}
								>
									<div
										style={{
											position: 'absolute',
											inset: 0,
											background: `linear-gradient(90deg, ${hexToRgba(safeAccent, 0.05, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.88, safeAccent)} 48%, ${hexToRgba(safeAccent, 0.05, safeAccent)} 100%)`,
											animation: 'plynotePremiumShimmer 1.55s cubic-bezier(0.4, 0, 0.2, 1) infinite',
										}}
									/>
								</div>
							</div>
						</div>
					)}
					{shouldShowParsingOverlay && (
						<div
							style={{
								position: 'absolute',
								inset: 0,
								zIndex: 50,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								padding: 'clamp(20px, 5vw, 64px) clamp(16px, 4vw, 48px)',
								background: hexToRgba(safeBgPrimary, 0.88, safeBgPrimary),
								backdropFilter: 'blur(10px) saturate(110%)',
								WebkitBackdropFilter: 'blur(10px) saturate(110%)',
							}}
						>
							<div
								style={{
									width: 'min(90vw, 640px)',
									display: 'flex',
									flexDirection: 'column',
									alignItems: 'center',
									gap: 'clamp(8px, 1.4vh, 12px)',
								}}
							>
								<div style={{ fontSize: 'clamp(1.04rem, 1.75vw, 1.42rem)', fontWeight: 700, letterSpacing: '0.008em', color: safeTextPrimary, textAlign: 'center', lineHeight: 1.2 }}>
									{loadingTitle}
								</div>
								<div style={{ fontSize: 'clamp(0.8rem, 1.05vw, 0.92rem)', lineHeight: 1.5, color: safeTextSecondary, maxWidth: 560, textAlign: 'center' }}>
									{loadingSubtitle}
								</div>

								<div
									style={{
										position: 'relative',
										width: 'min(74vw, 460px)',
										height: 'clamp(4px, 0.55vw, 6px)',
										borderRadius: 999,
										overflow: 'hidden',
										background: hexToRgba(safeTextSecondary, 0.18, safeTextSecondary),
									}}
								>
									<div
										style={{
											position: 'absolute',
											inset: 0,
											background: `linear-gradient(90deg, ${hexToRgba(safeAccent, 0.05, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.88, safeAccent)} 48%, ${hexToRgba(safeAccent, 0.05, safeAccent)} 100%)`,
											animation: 'plynotePremiumShimmer 1.55s cubic-bezier(0.4, 0, 0.2, 1) infinite',
										}}
									/>
								</div>
							</div>
						</div>
					)}

				<div
					style={{
						display: 'flex',
						justifyContent: 'center',
						width: '100%',
						height: viewportHeight,
						minHeight: viewportHeight,
						maxHeight: viewportHeight,
						overflow: 'hidden',
					}}
				>
					{isEditorMountReady ? (
						<CourseEditor
												ref={editorRef}
						key={editorInstanceKey}
						initialContent={resolvedEditorContent}
						onChange={setEditorContent_json_tiptap}
						onManualSync={setEditorContent_json_tiptap}
						editorContentMaxWidth="118ch"
						readOnly={false}
						placeholder="Start writing your note..."
						editorBgColor={safeBgPrimary}
						editorTextColor={safeTextPrimary}
						toolbarBgColor={hexToRgba(safeBgPrimary, 0.94, safeBgPrimary)}
						editorHeight={`calc(${viewportHeight} - ${toolbarHeightPx}px)`}
						toolbarHeight={`${toolbarHeightPx}px`}
						editorWidth="min(100%, 1600px)"
						toolbarWidth="100%"
						containerStyle={{
							width: '100%',
							margin: '0 auto',
							height: viewportHeight,
							minHeight: viewportHeight,
							maxHeight: viewportHeight,
							border: 'none',
							borderRadius: 0,
							boxShadow: 'none',
							overflow: 'hidden',
							background: 'transparent',
						}}
						toolbarStyle={{
							margin: 0,
							border: 'none',
							borderBottom: `1px solid ${hexToRgba(safeTextSecondary, 0.22, safeTextSecondary)}`,
							borderRadius: 0,
							boxShadow: 'none',
							padding: '10px 24px',
							gap: 14,
							rowGap: 12,
							columnGap: 14,
						}}
							/>
					) : (
						<div
							aria-hidden="true"
							style={{
								width: '100%',
								height: '100%',
								background: `linear-gradient(180deg, ${hexToRgba(safeBgPrimary, 0.97, safeBgPrimary)} 0%, ${hexToRgba(safeBgSecondary, 0.97, safeBgSecondary)} 100%)`,
								borderTop: `1px solid ${hexToRgba(safeTextSecondary, 0.14, safeTextSecondary)}`,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<div style={{ width: 'min(90vw, 640px)', textAlign: 'center' }}>
								<div style={{ fontSize: 'clamp(1.04rem, 1.75vw, 1.42rem)', fontWeight: 700, letterSpacing: '0.008em', color: safeTextPrimary, marginBottom: 'clamp(6px, 1vh, 10px)', lineHeight: 1.2 }}>
									{loadingTitle}
								</div>
								<div style={{ fontSize: 'clamp(0.8rem, 1.05vw, 0.92rem)', lineHeight: 1.5, color: safeTextSecondary, marginBottom: 'clamp(8px, 1.2vh, 12px)' }}>
									{loadingSubtitle}
								</div>
								<div style={{ position: 'relative', width: 'min(74vw, 460px)', height: 'clamp(4px, 0.55vw, 6px)', margin: '0 auto', borderRadius: 999, overflow: 'hidden', background: hexToRgba(safeTextSecondary, 0.18, safeTextSecondary) }}>
									<div style={{ position: 'absolute', inset: 0, background: `linear-gradient(90deg, ${hexToRgba(safeAccent, 0.05, safeAccent)} 0%, ${hexToRgba(safeAccent, 0.88, safeAccent)} 48%, ${hexToRgba(safeAccent, 0.05, safeAccent)} 100%)`, animation: 'plynotePremiumShimmer 1.55s cubic-bezier(0.4, 0, 0.2, 1) infinite' }} />
								</div>
							</div>
						</div>
					)}
				</div>


			</main>
			{isChatVisible ? (
			<div
				style={{
					position: 'fixed',
					top: 14,
					right: 14,
					bottom: 14,
					left: `${chatPanelLeft}px`,
					width: `${chatPanelRenderedWidth}px`,
					maxWidth: 'calc(100vw - 28px)',
				zIndex: 10001,
					display: 'flex',
					flexDirection: 'column',
					gap: 8,
					opacity: isChatAnimatedIn ? 1 : 0,
					transform: isChatAnimatedIn ? 'translateX(0) scale(1)' : 'translateX(24px) scale(0.985)',
					filter: isChatAnimatedIn ? 'blur(0px)' : 'blur(2px)',
					pointerEvents: isChatAnimatedIn ? 'auto' : 'none',
					transition: 'opacity 260ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1), filter 320ms cubic-bezier(0.22, 1, 0.36, 1), width 320ms cubic-bezier(0.22, 1, 0.36, 1), left 320ms cubic-bezier(0.22, 1, 0.36, 1)',
				}}
			>
				{!isChatFullscreen ? (
					<div
						onMouseDown={handleChatResizeStart}
						role="separator"
						aria-label="Resize chat panel"
						style={{
							position: 'absolute',
							left: -6,
							top: 0,
							bottom: 0,
							width: 12,
							cursor: 'col-resize',
							zIndex: 2,
						}}
					/>
				) : null}
				<div style={{ flex: 1, minHeight: 0 }}>
					<AIChat
						chatConversationJson={chat_conversation_json}
						onSubmit={handleChatSubmit}
						isSubmitting={isChatSubmitting}
						title={t('notes.assistantTitle')}
						isFullscreen={isChatFullscreen}
						onToggleFullscreen={() => setIsChatFullscreen((previousValue) => !previousValue)}
						onClose={() => setIsChatOpen(false)}
					/>
				</div>
			</div>
			) : null}
			<AIChatFloatingButton
				title={t('notes.openAssistant')}
				ariaLabel={t('notes.openAssistant')}
				onClick={() => setIsChatOpen((previousValue) => !previousValue)}
				style={{
					opacity: isChatVisible ? 0 : 1,
					pointerEvents: isChatVisible ? 'none' : 'auto',
					transform: isChatVisible ? 'translateY(-50%) translateX(24px) scale(0.96)' : 'translateY(-50%) translateX(0) scale(1)',
					transition: 'opacity 260ms ease, transform 320ms cubic-bezier(0.22, 1, 0.36, 1)',
				}}
			/>
		</div>
	);
}

export default NoteEditorView;
